import { defaultStopTime } from "../components/trip-planner/trip-date";

/**
 * Getting from one stop to the next, and whether the day leaves time for it.
 *
 * We have no routing API, so a leg is estimated from the straight-line
 * distance: on foot up to ~1.3 km as the crow flies (~1.7 km of streets),
 * by car beyond. This is the app's rule (it used to live in the app's
 * trip-metrics); the website quoted every leg on foot, which turned an 8 km
 * hop into a "102 min" walk inside a 30 minute gap (UX review, 2026-11-03).
 *
 * When the gap between one stop's end and the next one's start is shorter
 * than the leg, the day says so and offers to start the next stop later
 * (owner, 2026-11-03: warn, with a one-tap fix).
 *
 * Pure: web and mobile both import it.
 */

export interface LatLonPoint {
  readonly lat: number;
  readonly lon: number;
}

export interface TravelLeg {
  readonly kind: "walk" | "drive";
  /** Rounded to 5 minutes; at least 5 on foot, 10 by car. */
  readonly minutes: number;
  /** Straight-line distance. */
  readonly meters: number;
}

const WALK_DETOUR = 1.3;
const WALK_MIN_PER_KM = 12.5; // 4.8 km/h
const DRIVE_MIN_PER_KM = 2;
const DRIVE_BASE_MIN = 6;
/** Straight-line, so the real walk this allows is up to ~1.7 km. */
const MAX_WALK_KM = 1.3;
const MIN_LEG_KM = 0.05;
const MAX_LEG_KM = 120;

function haversineKm(a: LatLonPoint, b: LatLonPoint): number {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lon - a.lon);
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLon / 2) ** 2;
  return 6371 * 2 * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * The leg between two stops, or null when they share a door (under 50 m) or
 * are so far apart (over 120 km) that a guess would mislead.
 */
export function travelLeg(from: LatLonPoint, to: LatLonPoint): TravelLeg | null {
  const km = haversineKm(from, to);
  if (km < MIN_LEG_KM || km > MAX_LEG_KM) return null;
  const meters = Math.round(km * 1000);
  if (km <= MAX_WALK_KM) {
    return { kind: "walk", minutes: Math.max(5, Math.round((km * WALK_DETOUR * WALK_MIN_PER_KM) / 5) * 5), meters };
  }
  return { kind: "drive", minutes: Math.max(10, Math.round((DRIVE_BASE_MIN + km * DRIVE_MIN_PER_KM) / 5) * 5), meters };
}

/** Google Maps directions for a leg, walking or driving as the leg is. */
export function legDirectionsUrl(from: LatLonPoint, to: LatLonPoint, kind: TravelLeg["kind"]): string {
  return (
    "https://www.google.com/maps/dir/?api=1" +
    `&origin=${from.lat},${from.lon}` +
    `&destination=${to.lat},${to.lon}` +
    `&travelmode=${kind === "walk" ? "walking" : "driving"}`
  );
}

const HHMM = /^([01]\d|2[0-3]):([0-5]\d)$/;

function toMin(hhmm: string | undefined | null): number | null {
  const m = hhmm ? HHMM.exec(hhmm) : null;
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
}

function toHHMM(min: number): string {
  return `${String(Math.floor(min / 60)).padStart(2, "0")}:${String(min % 60).padStart(2, "0")}`;
}

/**
 * The times a stop runs at as the day shows them: its own, else the slot the
 * day gives it by position. A start without an end has no known end.
 */
export function stopTimes(
  stop: { readonly startTime?: string; readonly endTime?: string },
  index: number,
): { start: number | null; end: number | null } {
  if (stop.startTime || stop.endTime) return { start: toMin(stop.startTime), end: toMin(stop.endTime) };
  const slot = defaultStopTime(index);
  return { start: toMin(slot.startTime), end: toMin(slot.endTime) };
}

/** What a tight leg needs: the next stop moved to start later. */
export interface LegFix {
  /** Minutes between the first stop's end and the next one's start. */
  readonly gap: number;
  /** The next stop's new times, kept the same length; end absent when it had none. */
  readonly start: string;
  readonly end?: string;
}

/**
 * Null when the leg fits (or the times are unknown). Otherwise the gap and
 * the next stop's times moved to the first quarter hour that leaves room.
 * Null too when that would run past 23:45: no fix worth offering.
 */
export function tightLeg(
  prev: { readonly startTime?: string; readonly endTime?: string },
  prevIndex: number,
  next: { readonly startTime?: string; readonly endTime?: string },
  nextIndex: number,
  leg: TravelLeg,
): LegFix | null {
  const a = stopTimes(prev, prevIndex);
  const b = stopTimes(next, nextIndex);
  if (a.end === null || b.start === null) return null;
  const gap = b.start - a.end;
  if (gap >= leg.minutes) return null;
  const start = Math.ceil((a.end + leg.minutes) / 15) * 15;
  const length = b.end !== null && b.end > b.start ? b.end - b.start : null;
  const end = length !== null ? start + length : null;
  if (start > 23 * 60 + 45 || (end !== null && end > 23 * 60 + 59)) return null;
  return { gap: Math.max(0, gap), start: toHHMM(start), ...(end !== null ? { end: toHHMM(end) } : {}) };
}
